import { blogAPI, ApiBlogPost } from "./api";

/**
 * Blog cache — warms and caches the gateway's public `/api/blog` so navigating
 * to /blog has no cold-start lag. The list endpoint omits `content` (server-side
 * `.select("-content")`); full bodies are fetched per-slug by the post page.
 */
interface BlogCacheState {
  posts: ApiBlogPost[] | null;
  postsBySlug: Map<string, ApiBlogPost>;
  fetchedAt: number;
  pending: Promise<void> | null;
}

const STALE_MS = 5 * 60 * 1000; // 5 minutes

const cache: BlogCacheState = {
  posts: null,
  postsBySlug: new Map(),
  fetchedAt: 0,
  pending: null,
};

function isStale(): boolean {
  return Date.now() - cache.fetchedAt > STALE_MS;
}

async function fetchAndCache(): Promise<void> {
  try {
    const { posts } = await blogAPI.getPosts({ limit: 100 });
    if (posts) {
      cache.posts = posts;
      cache.postsBySlug.clear();
      for (const post of posts) {
        cache.postsBySlug.set(post.slug, post);
      }
      cache.fetchedAt = Date.now();
    }
  } catch {
    // Warm-up failed silently — pages fetch on their own and show the honest
    // unavailable state if that fails too (CO 536: no static fallback).
  } finally {
    cache.pending = null;
  }
}

/**
 * Warm up the blog API and cache results. Call on app init to eliminate
 * cold-start lag when users navigate to /blog.
 */
export function warmUpBlog(): void {
  if (!cache.pending && isStale()) {
    cache.pending = fetchAndCache();
  }
}

/** Cached posts list, or null if the cache is empty. Triggers a refresh if stale. */
export function getCachedPosts(): ApiBlogPost[] | null {
  if (isStale()) {
    warmUpBlog();
  }
  return cache.posts;
}

/** A cached post by slug, or null if not in cache. */
export function getCachedPost(slug: string): ApiBlogPost | null {
  return cache.postsBySlug.get(slug) ?? null;
}

/** Wait for any in-flight cache fetch to complete. */
export function waitForCache(): Promise<void> {
  return cache.pending ?? Promise.resolve();
}
